// HUD: hält sich raus (Konzept §4, §8). Keine Karte, kein Kompass, keine
// Zähler, keine Namen. Die Uhr ist die einzige dauerhaft sichtbare Zahl —
// ohne sie ist der Fahrplan nicht entschlüsselbar (§5).

import { formatClock } from '../../shared/worldtime.js';
import { PAINT_COLORS, PAINT_CAN_CAPACITY } from '../../shared/constants.js';

// ---------------------------------------------------------------------------
// Zustandstexte — rein, monoton in der Schwere, headless testbar
// ---------------------------------------------------------------------------

/**
 * Schweregrad Kälte 0..5. Streng monoton fallend in warmth:
 * kälter ⇒ nie ein harmloserer Grad.
 */
export function warmthSeverity(warmth) {
  const w = Number(warmth);
  if (!Number.isFinite(w) || w <= 8) return 5;
  if (w <= 18) return 4;
  if (w <= 32) return 3;
  if (w <= 48) return 2;
  if (w <= 62) return 1;
  return 0;
}

/** Schweregrad Nässe 0..4. Streng monoton steigend in wetness. */
export function wetnessSeverity(wetness) {
  const w = Number(wetness);
  if (!Number.isFinite(w) || w < 18) return 0;
  if (w < 35) return 1;
  if (w < 55) return 2;
  if (w < 78) return 3;
  return 4;
}

const WARMTH_LINES = [
  null,
  'deine Hände sind kalt',
  'die Kälte kriecht in die Gelenke',
  'du zitterst',
  'der Fahrtwind schneidet',
  'dir ist speiübel vor Kälte',
];

const WETNESS_LINES = [
  null,
  'deine Kleidung ist feucht',
  'du bist nass',
  'du bist durchnässt',
  'alles an dir tropft',
];

/**
 * Liefert Anzeigezeilen inkl. CSS-Klasse und Schweregrad.
 * Mehrere Sätze möglich, aber sparsam — Konzept §16.6: Ernst, keine Dramatik.
 */
export function stateFromVitals(warmth, wetness) {
  const ws = warmthSeverity(warmth);
  const vs = wetnessSeverity(wetness);
  const lines = [];

  if (ws > 0) {
    lines.push({
      text: WARMTH_LINES[ws],
      cls: ws >= 4 ? 'warn cold' : 'cold',
      severity: ws,
      kind: 'warmth',
    });
  }
  if (vs > 0) {
    lines.push({
      text: WETNESS_LINES[vs],
      cls: vs >= 3 ? 'warn wet' : 'wet',
      severity: vs,
      kind: 'wetness',
    });
  }

  // Näss + Kälte: ein zusätzlicher Hinweis, aber nur wenn beide spürbar
  if (ws >= 2 && vs >= 2 && ws + vs >= 5) {
    lines.push({
      text: 'nasse Kälte frisst die Wärme',
      cls: 'warn',
      severity: ws + vs,
      kind: 'combo',
    });
  }

  return lines;
}

/** Gesamtschwere für Monotonie-Tests (höher = schlimmer). */
export function vitalsRank(warmth, wetness) {
  const lines = stateFromVitals(warmth, wetness);
  if (lines.length === 0) return 0;
  // Primär Kälte, sekundär Nässe — Kombination erhöht weiter.
  return warmthSeverity(warmth) * 10 + wetnessSeverity(wetness);
}

// ---------------------------------------------------------------------------
// Steuerung — eine einzige Quelle für Startbildschirm und Overlay
// ---------------------------------------------------------------------------

/**
 * Tastenbelegung, gruppiert nach Thema. Steht bewusst hier und nicht im HTML:
 * Startbildschirm und Spiel-Overlay zeigen dieselbe Liste, sonst driften die
 * beiden auseinander, sobald sich eine Taste ändert.
 */
export const CONTROL_LINES = [
  [['WASD', 'gehen'], ['Shift', 'rennen'], ['Strg', 'ducken'], ['Leertaste', 'springen']],
  [['E', 'greifen / aufspringen / loslassen'], ['Maus', 'umsehen']],
  [['F', 'Dose nehmen'], ['Maus links', 'sprühen'], ['1-0', 'Farbe'], ['Q/W', 'Strichstärke']],
  [['R', 'Funkgerät'], ['T', 'Kanal wählen'], ['C', 'fotografieren'], ['B', 'Fotochronik'], ['L', 'Taschenlampe']],
  [['H', 'Steuerung zeigen'], ['F3', 'Debug'], ['Esc', 'Maus freigeben']],
  // Entwicklerwerkzeug — bewusst markiert, gehört nicht zur Spielsteuerung.
  // Shift allein ist schon „rennen"/Turbo im Flug; hier nur die Umschalt-Taste.
  [['G', 'Godmode fliegen (Dev)']],
];

/** Baut die Tastenliste als DOM in ein Element. Ohne innerHTML. */
export function renderControls(el) {
  if (!el) return;
  el.textContent = '';
  for (const group of CONTROL_LINES) {
    const row = document.createElement('div');
    for (let i = 0; i < group.length; i++) {
      if (i > 0) row.appendChild(document.createTextNode(' · '));
      const b = document.createElement('b');
      b.textContent = group[i][0];
      row.append(b, document.createTextNode(` ${group[i][1]}`));
    }
    el.appendChild(row);
  }
}

// ---------------------------------------------------------------------------
// HudSystem
// ---------------------------------------------------------------------------

export class HudSystem {
  async init(ctx) {
    this.ctx = ctx;
    this.els = {
      clock: document.getElementById('hud-clock'),
      state: document.getElementById('hud-state'),
      prompt: document.getElementById('hud-prompt'),
      paint: document.getElementById('hud-paint'),
      log: document.getElementById('hud-log'),
      reticle: document.getElementById('reticle'),
      help: document.getElementById('hud-help'),
      helpKeys: document.getElementById('hud-help-keys'),
      helpButton: document.getElementById('hud-help-button'),
    };

    this._clockText = '';
    this._stateSig = '';
    this._promptSig = '';
    this._paintSig = '';
    this._reticleSig = '';
    this._logLines = []; // { text, t, el }
    this._now = 0;
    this._paintBuilt = false;
    // §13: einmal, unaufdringlich — kein Werbebanner, kein Kaufaufruf, keine Zahl.
    this._writeNoticeShown = false;

    // Log-Hook der Engine umbiegen ist schon in main.js — hier die Methode.
    if (this.els.paint) this.els.paint.style.display = 'none';

    this._initHelp(ctx);
  }

  // --- Steuerungs-Overlay ---------------------------------------------------
  //
  // §8 sagt: kein UI, das sich vordrängt. Deshalb ist die Liste nicht dauerhaft
  // sichtbar, sondern liegt hinter einem kleinen Zeichen — sichtbar genug, dass
  // man sie findet, still genug, dass man sie vergisst.

  _initHelp(ctx) {
    this.helpOpen = false;
    renderControls(this.els.helpKeys);

    const btn = this.els.helpButton;
    if (btn) {
      this._onHelpClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleHelp(ctx);
        // Sonst behält der Button den Fokus und Leertaste drückt ihn erneut.
        btn.blur();
      };
      btn.addEventListener('click', this._onHelpClick);
    }

    // Klick daneben schließt — erwartetes Verhalten bei Overlays.
    if (this.els.help) {
      this._onHelpBackdrop = (e) => {
        if (e.target === this.els.help) this.setHelp(false, ctx);
      };
      this.els.help.addEventListener('click', this._onHelpBackdrop);
    }

    // H läuft über einen eigenen Listener statt über input.justPressed():
    // bei offenem Overlay steht textCapture, und dann liefert Input dem Spiel
    // gar keine Tasten mehr — die Liste könnte sich selbst nicht schließen.
    this._onHelpKey = (e) => {
      if (e.code === 'KeyH') {
        // Nicht während Funktext getippt wird — dort ist H ein Buchstabe.
        if (!this.helpOpen && ctx.input?.textCapture) return;
        e.preventDefault();
        this.toggleHelp(ctx);
      } else if (e.code === 'Escape' && this.helpOpen) {
        // Escape schließt nur; die Maus bleibt frei (Browser-Verhalten).
        this.setHelp(false, ctx, false);
      }
    };
    window.addEventListener('keydown', this._onHelpKey);
  }

  /**
   * @param relock beim Schließen die Maus wieder einfangen. Nach Escape nicht:
   *   der Browser sperrt Pointer-Lock kurz danach ohnehin, und Escape bedeutet
   *   „lass mich raus", nicht „nimm mir den Zeiger sofort wieder weg".
   */
  setHelp(open, ctx, relock = true) {
    if (open === this.helpOpen) return;
    this.helpOpen = open;
    this.els.help?.classList.toggle('hidden', !open);
    this.els.helpButton?.classList.toggle('on', open);

    const input = ctx?.input ?? this.ctx?.input;
    if (!input) return;
    if (open) {
      // Offene Liste liest man mit der Maus, nicht mit dem Kopf.
      input.releaseLock();
      // Gehaltene Tasten fallen lassen und Eingabe stummschalten: sonst läuft
      // man weiter, während man liest — vom Dach eines fahrenden Waggons ist
      // das teuer. textCapture ist derselbe Schalter, den der Funk benutzt.
      input.keys?.clear();
      input.mouse.left = false;
      input.mouse.right = false;
      input.textCapture = true;
    } else {
      // Den Funk nicht überfahren: tippt dort jemand, bleibt textCapture an.
      if (!(ctx?.systems ?? this.ctx?.systems)?.radio?.tuning) input.textCapture = false;
      if (relock && (ctx?.started ?? this.ctx?.started)) input.requestLock();
    }
  }

  toggleHelp(ctx) { this.setHelp(!this.helpOpen, ctx); }

  log(text) {
    // Kann vor init() kommen (andere Systeme loggen beim Start über ctx.log).
    if (!text || !this.els?.log) return;
    if (!this._logLines) this._logLines = [];
    const line = {
      text: String(text),
      t: this._now ?? 0,
      el: document.createElement('div'),
    };
    line.el.textContent = line.text;
    this.els.log.appendChild(line.el);
    this._logLines.push(line);
    while (this._logLines.length > 5) {
      const old = this._logLines.shift();
      old.el.remove();
    }
  }

  update(dt, ctx) {
    this._now += dt;
    this._updateClock(ctx);
    this._updateState(ctx);
    this._updatePrompt(ctx);
    this._updatePaint(ctx);
    this._updateReticle(ctx);
    this._updateWriteNotice(ctx);
    this._updateLog();
  }

  /**
   * Einmalige, leise Erklärung wenn Malen nicht möglich ist (§13).
   * Kein Kaufaufruf, keine Zahl, kein Banner — nur der Sachverhalt.
   */
  _updateWriteNotice(ctx) {
    if (this._writeNoticeShown) return;
    const net = ctx.net;
    if (!net || net.canWrite !== false) return;
    // Erst erklären, wenn der Spieler es merken würde: Dose in der Hand
    // oder ein abgewiesener Schreibversuch.
    const paint = ctx.systems?.paint;
    const holding = !!(paint && (paint.holdingCan || paint.hasCan || paint.toolActive));
    if (!holding && !net.writeDeniedOnce) return;
    this._writeNoticeShown = true;
    this.log('Lesen ist frei. Schreiben nicht.');
  }

  _updateClock(ctx) {
    const el = this.els.clock;
    if (!el) return;
    const ws = ctx.worldSeconds ?? 0;
    // Minutenauflösung reicht — Sekunden würden flackern und wirken stoppuhrig.
    const text = formatClock(ws, false);
    if (text === this._clockText) return;
    this._clockText = text;
    el.textContent = text;
  }

  _updateState(ctx) {
    const el = this.els.state;
    if (!el) return;
    const ps = ctx.playerState;
    const warmth = ps?.warmth ?? 100;
    const wetness = ps?.wetness ?? 0;
    const lines = stateFromVitals(warmth, wetness);
    const sig = lines.map((l) => l.text + l.cls).join('|');
    if (sig === this._stateSig) return;
    this._stateSig = sig;

    el.textContent = '';
    for (const l of lines) {
      const span = document.createElement('div');
      span.className = l.cls;
      span.textContent = l.text;
      el.appendChild(span);
    }
  }

  _updatePrompt(ctx) {
    const el = this.els.prompt;
    if (!el) return;

    const parts = [];
    const ps = ctx.playerState;

    if (ps?.prompt) {
      // Player liefert kurze Tokens — Haltefortschritt als Wort, kein Balken (§8).
      const p = ps.prompt;
      if (p === 'greifen') parts.push(keyPrompt('E', 'greifen'));
      else if (p === 'greifen …') parts.push(keyPrompt('E', 'greifen …'));
      else if (p === 'halt fest') parts.push(keyPrompt('E', 'halt fest'));
      else if (p === 'fast') parts.push(keyPrompt('E', 'fast'));
      else if (p === 'hochziehen') parts.push(keyPrompt('E', 'hochziehen'));
      else if (p === 'hochdrücken') parts.push(keyPrompt('E', 'hochdrücken'));
      else if (p === 'hoch …') parts.push(keyPrompt('E', 'hoch …'));
      else if (p === 'klettern') parts.push('W/S klettern');
      else if (p === 'zu schnell') parts.push('zu schnell');
      else if (p === 'erholen …' || p === 'erholen') parts.push('erholen …');
      else parts.push(p);
    }

    // Loslassen am Rand des Waggons
    if (ps?.edgeWarn && (ps.mode === 'ride' || ps.mode === 'inside')) {
      parts.push(keyPrompt('E', 'loslassen'));
    }

    // Dose: PaintSystem defensiv
    const paint = ctx.systems?.paint;
    if (paint) {
      if (paint.holdingCan || paint.hasCan) {
        // schon in der Hand — kein Prompt nötig
      } else if (paint.canPickup !== false) {
        // F ist immer verfügbar wenn Paint lebt; spezieller Prompt wenn Ziel da
        if (ctx.paintTarget) {
          parts.push(keyPrompt('F', 'Dose'));
        }
      }
    }

    // Malziel ohne Dose-Hinweis
    if (ctx.paintTarget && paint?.holdingCan) {
      // Reticle übernimmt; kein Text nötig
    }

    const html = parts.filter(Boolean).join(' · ');
    if (html === this._promptSig) return;
    this._promptSig = html;
    el.innerHTML = html;
  }

  _updatePaint(ctx) {
    const el = this.els.paint;
    if (!el) return;
    const paint = ctx.systems?.paint;
    const holding = !!(paint && (paint.holdingCan || paint.hasCan || paint.toolActive));

    if (!holding) {
      if (this._paintSig !== 'off') {
        this._paintSig = 'off';
        el.style.display = 'none';
      }
      return;
    }

    const colors = paint.colors ?? PAINT_COLORS;
    const idx = typeof paint.colorIndex === 'number' ? paint.colorIndex : 0;
    // canFill ist 0..PAINT_CAN_CAPACITY (nicht 0..100) — sonst zeigt die Dose ~26% „voll".
    const fill = paint.canFill ?? paint.fill ?? paint.canAmount ?? PAINT_CAN_CAPACITY;
    const fill01 = typeof fill === 'number'
      ? Math.max(0, Math.min(1, fill > 1.001 ? fill / PAINT_CAN_CAPACITY : fill))
      : 1;
    const sig = `on|${idx}|${fill01.toFixed(2)}|${colors.length}`;
    if (sig === this._paintSig) return;
    this._paintSig = sig;

    el.style.display = '';
    if (!this._paintBuilt || el.childElementCount === 0) {
      el.textContent = '';
      const sw = document.createElement('div');
      sw.className = 'swatches';
      for (let i = 0; i < colors.length; i++) {
        const s = document.createElement('div');
        s.className = 'swatch';
        s.style.background = colors[i];
        s.dataset.i = String(i);
        sw.appendChild(s);
      }
      const can = document.createElement('div');
      can.className = 'can';
      const cf = document.createElement('div');
      cf.className = 'can-fill';
      can.appendChild(cf);
      el.append(sw, can);
      this._paintBuilt = true;
      this._swatches = sw;
      this._canFill = cf;
    }

    const kids = this._swatches?.children;
    if (kids) {
      for (let i = 0; i < kids.length; i++) {
        kids[i].classList.toggle('on', i === idx);
      }
    }
    if (this._canFill) {
      this._canFill.style.width = `${Math.round(Math.max(0, Math.min(1, fill01)) * 100)}%`;
    }
  }

  _updateReticle(ctx) {
    const el = this.els.reticle;
    if (!el) return;
    const paint = ctx.systems?.paint;
    const aiming = !!(ctx.paintTarget || ctx.playerState?.canGrab);
    const painting = !!(paint && (paint.spraying || paint.painting));
    const sig = `${aiming}|${painting}`;
    if (sig === this._reticleSig) return;
    this._reticleSig = sig;
    el.classList.toggle('active', aiming && !painting);
    el.classList.toggle('painting', painting);
  }

  _updateLog() {
    // Zeilen nach ~8 s entfernen
    const ttl = 8;
    while (this._logLines.length && this._now - this._logLines[0].t > ttl) {
      const old = this._logLines.shift();
      old.el.remove();
    }
  }

  dispose() {
    this._logLines = [];
    if (this._onHelpClick) this.els?.helpButton?.removeEventListener('click', this._onHelpClick);
    if (this._onHelpBackdrop) this.els?.help?.removeEventListener('click', this._onHelpBackdrop);
    if (this._onHelpKey) window.removeEventListener('keydown', this._onHelpKey);
  }
}

function keyPrompt(key, label) {
  return `<b>${key}</b> ${label}`;
}
